<template>
  <div v-if="formUrl" class="c-login-block">
    <div
      class="c-login-block__hero-wrapper c-hero t-padding--v-l variant--secondary"
    >
      <div v-if="header" class="o-width-limiter c-hero__inner">
        <div class="c-hero__content c-hero__content--align-center">
          <h1 class="c-hero__title">{{ header }}</h1>

          <div class="c-hero__text">
            <p>
              {{ description }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="formUrl"
      class="c-login-block__bottom-wrapper"
      :style="backgroundStyle"
    >
      <div class="c-login-block__form-wrapper" :style="formWrapperStyle">
        <div class="c-login-form-block" :style="formBlockStyle">
          <div class="c-login-form-block__inner">
            <form
              class="form-block"
              @submit.prevent="submitForm"
              :action="formUrl"
              novalidate
            >
              <!-- First Name -->
              <div class="c-login-form-block__group">
                <input
                  name="firstname"
                  type="text"
                  v-model="firstName"
                  :class="{ filled: firstName || errors.firstName }"
                  :placeholder="firstNameLabel"
                  :aria-invalid="!!errors.firstName"
                  required
                />
                <span
                  v-if="errors.firstName"
                  class="c-login-form-block__error"
                  role="alert"
                >
                  {{ errors.firstName }}
                </span>
              </div>

              <!-- Last Name -->
              <div class="c-login-form-block__group">
                <input
                  type="text"
                  name="lastname"
                  v-model="lastName"
                  :class="{ filled: lastName || errors.lastName }"
                  :placeholder="lastNameLabel"
                  :aria-invalid="!!errors.lastName"
                  required
                />
                <span
                  v-if="errors.lastName"
                  class="c-login-form-block__error"
                  role="alert"
                >
                  {{ errors.lastName }}
                </span>
              </div>

              <!-- Email -->
              <div class="c-login-form-block__group">
                <input
                  type="email"
                  name="email"
                  v-model="email"
                  :class="{ filled: email || errors.email }"
                  :placeholder="EmailLabel"
                  :aria-invalid="!!errors.email"
                  required
                />
                <span
                  v-if="errors.email"
                  class="c-login-form-block__error"
                  role="alert"
                >
                  {{ errors.email }}
                </span>
              </div>

              <div class="c-login-form-block__btn-wrapper">
                <button type="submit" class="c-button">
                  {{ SubmitLabel }}
                </button>
              </div>

              <div
                v-if="successMessage"
                class="c-login-form-block__success"
                role="status"
              >
                {{ successMessage }}
              </div>
              <div
                v-if="errorMessage"
                class="c-login-form-block__error"
                role="alert"
              >
                {{ errorMessage }}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "VLoginBlock",

  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      errors: {},
      successMessage: "",
      errorMessage: "",
      recaptchaToken: "",
    };
  },
  props: {
    header: { type: String, required: true },
    description: { type: String, required: false },
    backgroundUrl: { type: String, required: false },
    color: { type: String, required: false },
    backgroundColor: { type: String, required: false },
    formUrl: {
      type: String,
      required: true,
    },
    firstNameLabel: { type: String, required: false, default: "fornavn" },
    lastNameLabel: { type: String, required: false, default: "efternavn" },
    EmailLabel: { type: String, required: false, default: "epost" },
    SubmitLabel: { type: String, required: false, default: "Indsendt" },
    RequiredLabel: {
      type: String,
      required: false,
      default: "Detta fält måste fyllas i",
    },
    WrongEmailLabel: {
      type: String,
      required: false,
      default: "Du måste ange en giltig e-postadress",
    },
    blockId: {
      type: Number,
      required: true,
    },
    siteKey: {
      type: String,
      required: true,
      default: "6LfwIEArAAAAAHaBWux9dpEOh11M_GXUS8EpNBRn",
    },
  },
  mounted() {
    // dynamically add reCAPTCHA v3 script
    const script = document.createElement("script");
    script.src =
      "https://www.google.com/recaptcha/api.js?render=6LfwIEArAAAAAHaBWux9dpEOh11M_GXUS8EpNBRn";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  },
  methods: {
    validateForm() {
      this.errors = {};
      let valid = true;

      if (!this.firstName.trim()) {
        this.errors.firstName = this.RequiredLabel;
        valid = false;
      }

      if (!this.lastName.trim()) {
        this.errors.lastName = this.RequiredLabel;
        valid = false;
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!this.email.trim()) {
        this.errors.email = this.RequiredLabel;
        valid = false;
      } else if (!emailPattern.test(this.email)) {
        this.errors.email = this.WrongEmailLabel;
        valid = false;
      }

      return valid;
    },
    getRecaptchaToken() {
      return new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha
            .execute(this.siteKey, { action: "submit" })
            .then((token) => {
              if (!token) reject("Unable to validate reCAPTCHA");
              this.recaptchaToken = token;
              resolve(token);
            })
            .catch(() => reject("Unable to validate reCAPTCHA"));
        });
      });
    },
    async submitForm() {
      if (!this.validateForm()) return;

      try {
        // Get reCAPTCHA token first
        await this.getRecaptchaToken();

        // Prepare form data
        const formData = new FormData();
        formData.append("firstName", this.firstName);
        formData.append("lastName", this.lastName);
        formData.append("email", this.email);
        formData.append("GoogleCaptchaToken", this.recaptchaToken);
        formData.append("blokId", this.blockId);

        const response = await fetch(this.formUrl, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text);
        }

        const data = await response.json();

        console.log("data", data);

        if (data.message) {
          this.successMessage = data.message;
          this.errorMessage = "";
          this.firstName = "";
          this.lastName = "";
          this.email = "";
          this.recaptchaToken = "";
        }
      } catch (err) {
        this.errorMessage =
          typeof err === "string" ? err : "Network error. Please try again!";
        this.successMessage = "";
      }
    },
  },
  computed: {
    backgroundStyle() {
      return {
        backgroundImage: `url(${this.backgroundUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    },
    formWrapperStyle() {
      return {
        backgroundColor: this.backgroundColor || "#333",
        color: this.color || "#fff",
      };
    },
    formBlockStyle() {
      return {
        color: this.color || "#fff",
      };
    },
  },
};
</script>
